
import APP_CONFIG from '../../app-config/app-config';

import { Injectable, EventEmitter } from '@angular/core';
import { Http } from '@angular/http';

import { Platform, ModalController, NavController } from 'ionic-angular';
import { InAppBrowser } from '@ionic-native/in-app-browser';
import { NativeStorage } from '@ionic-native/native-storage';
import { ScreenOrientation } from '@ionic-native/screen-orientation';

import { NetworkService } from './network.service';
import { UpdateService } from './update.service';

import 'rxjs/add/operator/map';

@Injectable()
export class MainService {
	readonly BULLETINS_URL = 'https://apps.media4learning.co.nz/av/clinical_bulletins';

	private readonly DATA_DIR: string = 'assets/data';
	private readonly CONTENT_DIR: string = `assets/html`;

	rootNavController: NavController;
	readonly TAB_INDEX = {
		info: 0,
		pharmacology: 1,
		calculators: 2,
		checklists: 3,
		further_info: 4,
	};

	isCordova: boolean = false;

	practiceLevelList: any = ['ACO/CERT', 'ALS', 'MICA', 'AAV', 'ARV'];
	practiceLevel: string = 'ALS';

	orientation: string = 'Auto';
	tabColor: string = '';

	newClinicalBulletins: boolean = false;

	notificationsAlertsOld: any;
	notificationsAlertsNew: any;

	PAGES: any = {};

	hideTabs: boolean = false;

	canvasFrontTemp: any;
	canvasBackTemp: any;

	checklistEvent$: EventEmitter<any>;

	clinicalUpdateData: any = [];

	constructor(
		private platform: Platform,
		private inAppBrowser: InAppBrowser,
		private http: Http,
		private networkService: NetworkService,
		private updateService: UpdateService,
		private screenOrientation: ScreenOrientation,
		public nativeStorage: NativeStorage,
		private modalController: ModalController,
	) {
		this.checklistEvent$ = new EventEmitter();
		this.init();

		this.getClinicalUpdateData();
		this.networkService.init();
	}

	private getJSON(name: string): void {
		let url = this.dataDirectory(`${name}.json`);
		this.http
			.get(url)
			.map((res) => res.json())
			.subscribe((res) => {
				if(!res.root) {
					res.root = name;
				}

				this.PAGES[name] = res;
			});
	}

	private init(): void {
		this.loadPageData();

		this.platform.ready().then(() => {
			if (this.platform.is('cordova')) {
				this.isCordova = true;
				this.getOrientation();

				if (APP_CONFIG.bulletins) {
					this.checkForNotifications();
				}
			}

			this.getPracticeLevel();
		});
	}

	// called from updatePracticeLevel() after determining which practice level to use
	private loadPageData(): void {
		this.PAGES = [];

		this.getJSON('info');
		this.getJSON('pharmacology');
		this.getJSON('calculators');
		this.getJSON('checklists');
		this.getJSON('further_info');
	}

	currentVersionString(prefix: boolean = true): string {
		return this.updateService.currentVersionString(prefix);
	}

	latestVersionString(prefix: boolean = true): string {
		return this.updateService.latestVersionString(prefix);
	}

	dataDirectory(url: string): string {
		return `${this.DATA_DIR}/${url}`;
	}

	contentDirectory(url: string): string {
		return `${this.CONTENT_DIR}/${url}`;
	}

	checklistEvent() {
    this.checklistEvent$.emit();
  }

	// set the color of the tab
	setTabColor(color: string): void {
		if (color) {
			this.tabColor = color;
		} else {
			// if there's no color - just leave it blank so it will show the default color
			this.tabColor = '';
		}
	}

	openInAppBrowser(url: string, openInSystemBrowser: boolean = false): void {
		let target = openInSystemBrowser ? '_system' : '_blank';

		if(this.platform.is('cordova') && !this.platform.is('android')) {
			target = '_blank';
		}

		this.inAppBrowser.create(url, target, 'location=no,toolbarposition=top');
	}

	// get notifications
	private checkForNotifications() {
		// get the current time to force a cache bust
		let date = new Date().getTime();
		let notificationURL = `${this.BULLETINS_URL}/json/notifications.json?no-cache=${date}`;

		this.http
			.get(notificationURL)
			.map((res) => res.json())
			.subscribe(
				(res) => {
					// save the result
					this.notificationsAlertsNew = res;
					this.notificationsAlertsOld = res;

					this.nativeStorage.keys().then((data) => {
						if (data.indexOf('notifications') === -1) {
							// if there's no previous clinical bulletins updates saved - just save the result
							this.nativeStorage.setItem('notifications', res);
						} else {
							// if there is clinical bulletins data saved - get the old data and then compare with new data
							this.nativeStorage.getItem('notifications').then((data) => {
								this.notificationsAlertsOld = data;
								this.compareNotificationData();
							});
						}
					});
				},
				(error) => {
					console.log(error);
				}
			);
	}

	// check to see if there are new alerts for the clinical bulletins
	private compareNotificationData() {
		if (this.notificationsAlertsNew.clinicalbulletins > this.notificationsAlertsOld.clinicalbulletins) {
			this.newClinicalBulletins = true;
		}
	}

	// count how many fields have alerts to display on the tab bar badge
	alertCount(): number {
		let count = 0;
		let conditions = [
			this.newClinicalBulletins,
			this.updateService.isBinaryUpdateAvailable,
			this.updateService.isContentUpdateAvailable
		];

		for (let i = 0; i < conditions.length; i++) {
			if (conditions[i]) {
				count++;
			}
		}

		return count;
	}

	// if there are app updates - make the badge red, otherwise grey
	alertBadgeStyle(): string {
		if (this.updateService.isBinaryUpdateAvailable || this.updateService.isContentUpdateAvailable) {
			return 'danger';
		}

		return 'grey';
	}

	// save the selected practice level
	setPracticeLevel(level): void {
		if (this.platform.is('cordova')) {
			this.nativeStorage.setItem('practiceLevel', level);
		}

		this.practiceLevel = level;
	}

	// get the currently selected practice level
	private getPracticeLevel(): void {
		if (this.platform.is('cordova')) {
			this.nativeStorage.getItem('practiceLevel').then(
				(data) => {
					this.practiceLevel = data;
				}
			);
		}
	}

	// save the selected orientation
	setOrientation(orientation) {
		if (this.platform.is('cordova')) {
			this.nativeStorage.setItem('orientation', orientation).then(() => {
				this.orientation = orientation;
				this.updateOrientation();
			});
		}
	}

	// get the currently selected orientation
	private getOrientation() {
		if (this.platform.is('cordova')) {
			this.nativeStorage.getItem('orientation').then(
				(data) => {
					this.orientation = data;
					this.updateOrientation();
				},
				(error) => {
					this.orientation = 'Auto';
					this.updateOrientation();
				}
			);
		}
	}

	// update the orientation of the app
	private updateOrientation() {
		switch (this.orientation) {
			case 'Portrait':
				this.screenOrientation.lock(this.screenOrientation.ORIENTATIONS.PORTRAIT);
				break;

			case 'Landscape':
				this.screenOrientation.lock(this.screenOrientation.ORIENTATIONS.LANDSCAPE);
				break;

			default:
				this.screenOrientation.unlock();
		}
	}

	// find a specific page in a list of pages
	findPage(key: string, value: string, findHidden: boolean = false, color?: string, obj?: any, root?: string): any {
		if(typeof value !== 'string' || value === '') {
			return false;
		}

		if (!obj) {
			obj = this.PAGES;
		}

		if (!obj || (!findHidden && obj.hidden)) {
			return false;
		}

		if (obj.color) {
			color = obj.color;
		}

		if (obj.root) {
			root = obj.root;
		}

		if (obj[key] === value) {
			return this.checkAppTypeForPage(obj);
		} else {
			for (let i = 0, len = Object.keys(obj).length; i < len; i++) {
				let newObj = obj[Object.keys(obj)[i]];

				if(!findHidden && newObj.hidden) {
					continue;
				}

				if (newObj.color) {
					color = newObj.color;
				}

				if (newObj.root) {
					root = newObj.root;
				}

				if (typeof newObj == 'object') {
					let found = this.findPage(key, value, findHidden, color, newObj, root);

					if (found) {
						if (color && !found.color) {
							found.color = color;
						}

						if (root && !found.root) {
							found.root = root;
						}

						return this.checkAppTypeForPage(found);
					}
				}
			}
		}
	}

	isPageExcluded(page: any): boolean {
		return (page.excluded_from && page.excluded_from.indexOf(APP_CONFIG.type) !== -1);
	}

	checkAppTypeForPage(page: any): any {
		if (this.isPageExcluded(page)) {
			return false;
		}

		return page;
	}

	removeExcludedPages(data: any): any {
		if (!data.pages) {
			return false;
		}

		let i = data.pages.length;

		while (i--) {
			if (data.pages[i].pages) {
				data.pages[i] = this.removeExcludedPages(data.pages[i]);
			}

			if (this.isPageExcluded(data.pages[i])) {
				data.pages.splice(i, 1);
			}
		}

		return data;
	}

	setTabVisibility(opt) {
		if(opt === 'show')
		{
		  this.hideTabs = false;
		}
		else
		{
		  this.hideTabs = true;
		}
	  }

	  authenticate(whichPage, callback?) {

		  let modal;
		  switch(whichPage) {
			  case 'register':
			  modal = this.modalController.create('RegisterPage', {}, {enableBackdropDismiss: false});
			  break;
			  case 'password':
			  modal = this.modalController.create('ResetPasswordPage', {}, {enableBackdropDismiss: false});
			  break;
			  case 'signin':
			  modal = this.modalController.create('SplashPage', {}, {enableBackdropDismiss: false});
			  break;
		  }

		  modal.present();

		  modal.onDidDismiss(() => {
			  if(typeof callback === "function") {
				  callback();
			  }
		  })
	  }

	  showVersionHistory(cpgRef) {
		console.log(cpgRef);
		}

		getClinicalUpdateData(): void {
			this.http
			.get('./assets/data/clinical-updates.json')
			.map((res) => res.json())
			.subscribe((res) => {
				this.clinicalUpdateData = res;
			});
		}

		getClinicalUpdateSectionLatestVersion(section: string): any {
			let result;

			for(let i: number = 0; i < this.clinicalUpdateData.length; i++) {
				if(this.clinicalUpdateData[i].cpg_reference.trim() === section) {
					let isNewer = true;

					if(result && !this.updateService.checkVersionIsNewer(result.version, this.clinicalUpdateData[i].version_number)) {
						isNewer = false;
					}

					if(isNewer) {
						result = {
							version: this.clinicalUpdateData[i].version_number,
							date: this.clinicalUpdateData[i].version_date
						};
					}
				}
			}

			return result;
		}

		sortCalcStrings(array: any) {
			var numeric = array.filter(function (val) {
				return /^[0-9]/i.test(val);
			});
			var alpha = array.filter(function (val) {
				return /^[a-z]/i.test(val);
			});
			var special = array.filter(function (val) {
				return /^[^a-z0-9]/i.test(val);
			});

			function localeCompareSupportsLocales() {
				try {
					'foo'.localeCompare('bar', 'i');
				} catch (e) {
					return e.name === 'RangeError';
				}
				return false;
			}

			function sortNumbers(a, b) {
				let numA = parseFloat(a);
				let numB = parseFloat(b);

				return numA - numB;
			}

			function sortItems(a, b) {
				if(localeCompareSupportsLocales()) {
					return a.localeCompare(b, undefined, {numeric: true, sensitivity: 'base'});
				}

				let textA = a.toLowerCase();
				let textB = b.toLowerCase();

				if(textA < textB) {
					return -1;
				}else if(textA > textB) {
					return 1;
				}

				return 0;
			}

			numeric.sort(sortNumbers);
			alpha.sort(sortItems);
			special.sort(sortItems);

			return numeric.concat(alpha, special);
		}

}



// WEBPACK FOOTER //
// ./src/services/main.service.ts